import { type ShopfrontDispatch } from "../../_Bootstrap/hooks";
import { store } from "../../_Bootstrap/RoutingAndStore";
import { showDialog } from "../../Actions/Dialogs/DialogActions";
import { OpenRegisterReasonDialog } from "../../Common/Dialogs/OpenRegisterReasonDialog";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { type SaleState } from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { getOption } from "../../Utilities/Platform/Options/Options";
import { sendAction, sendSecurityEvent } from "../Upload/UploadAgent";
import { HARDWARE_CONNECT_EVENTS } from "./HardwareConnectEventTypes";

export const HARDWARE_CONNECT_EVENT = "hardwareConnectEvent";

let disabledDebounce: false | ReturnType<typeof setTimeout> = false;

function sendEvent(event: {
    type: HARDWARE_CONNECT_EVENTS;
    data?: any;
    success?: boolean | ((err?: any) => void);
}): void {
    if(process.env.HARDWARE_CONNECT_DISABLED) {
        if(!disabledDebounce) {
            PopupManager.info("Hardware connect has been disabled for this Shopfront instance");
            disabledDebounce = setTimeout(() => {
                disabledDebounce = false;
            }, 1000);
        }

        if(typeof event.success === "function") {
            event.success(undefined);
        }
    }

    if(typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent(HARDWARE_CONNECT_EVENT, {
            detail: event,
        }));
    }
}

export function sendGiftCards(giftCards: Array<Record<string, any>>): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.GIFT_CARDS,
        data: giftCards,
    });
}

export async function sendDockets(sale: SaleState, userId: string, status: string): Promise<void> {
    const source = await determineDataSource();
    let user = "";

    try {
        const dbUser = await source.users.get(userId) as any;

        user = dbUser.name;
    } catch(e) {
        console.error("Could not get user to send for docket printing");
    }

    const products = [];

    for(let i = 0, l = sale.products.length; i < l; i++) {
        let dbProduct;

        try {
            dbProduct = await source.products.get(sale.products[i].uuid);
        } catch(e) {
            // Do nothing
        }

        if(!dbProduct) {
            continue;
        }

        const components = [];

        for(let j = 0, n = sale.products[i].products.length; j < n; j++) {
            if(typeof sale.products[i].defaultProducts[j] !== "undefined") {
                if(!sale.products[i].products[j].deleted) {
                    continue;
                }
            }

            let dbComponent;

            try {
                dbComponent = await source.products.get(sale.products[i].products[j].uuid) as any;
            } catch(e) {
                // Do nothing
            }

            if(!dbComponent) {
                continue;
            }

            components.push({
                name   : dbComponent.name,
                removed: sale.products[i].products[j].deleted,
            });
        }

        products.push({
            tags      : dbProduct.tags.length ? dbProduct.tags : [ "none" ],
            components: components,
            quantity  : sale.products[i].quantity,
            note      : sale.products[i].note,
            name      : dbProduct.name,
        });
    }

    const formattedSale = {
        invoiceId: sale.invoiceId,
        note     : sale.notes.sale,
        isParked : status === "PARKED",
        products,
    };

    const additional = {
        user,
    };

    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.PRINT_DOCKETS,
        data: {
            sale: formattedSale,
            additional,
        },
    });
}

export function sendSale(sale: Record<string, any>): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.SALE,
        data: sale,
    });
}

export type OpenDrawerCallback = (reason: string | null, error?: unknown) => void;

export interface OpenDrawerOptions {
    callback?: OpenDrawerCallback;
    from: string;
    register: string;
    reason?: string;
    requestReason?: boolean;
    code?: Array<number>;
}

export function openDrawer(options: OpenDrawerOptions): void {
    const { callback, from, register, reason, requestReason, code } = options;

    if(getOption("register_never_open_cash_drawer")) {
        PopupManager.info("The register has the \"never open cash drawer\" setting enabled");

        if(typeof callback === "function") {
            callback(null);
        }

        return;
    }

    if(
        requestReason !== false &&
        typeof reason !== "string" && (
            getOption("require_cash_drawer_reason") ||
            requestReason
        )
    ) {
        (store.dispatch as ShopfrontDispatch)(showDialog(
            "Open Cash Drawer",
            <OpenRegisterReasonDialog
                options={options}
            />,
            "question"
        ));

        return;
    }

    const success = (err?: unknown) => {
        try {
            void sendAction("OPENED_DRAWER", "App\\Models\\Register", register, {
                from,
                reason,
            });

            void sendSecurityEvent("OPEN_DRAWER", {
                from,
                reason,
            });
        } catch(e) {
            console.error("Cannot send action/event for opening cash drawer", e);
        }

        if(typeof callback === "function") {
            callback(reason || null, err);
        }
    };

    requestAnimationFrame(() => {
        sendEvent({
            type: HARDWARE_CONNECT_EVENTS.OPEN_DRAWER,
            data: {
                kickCode: code ?? null,
            },
            success,
        });
    });
}

export function printTextReceipt(receipt: string, success: boolean | ((error?: any) => void) = false): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.PRINT_PREFORMATTED,
        data: {
            toPrint: receipt,
        },
        success,
    });
}

export function systemPrint(success: boolean | ((error?: any) => void) = false): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.SEND_PRINT,
        success,
    });
}

export type HardwareConnectConfigType = {
    dockets: Record<string, string>;
    cashDrawerKick: Array<string>;
    cutDelay: string;
    cashDrawerTrigger: string;
    receiptPrinter: string;
    ignoreKick: boolean;
    ticketLocation: string;
    posLabelLocation: string;
};

export type HardwareConnectSettingsType = {
    config: HardwareConnectConfigType;
    ports: Array<any>;
    printers: Array<string>;
    updateAttempts: number;
    version: string;
};

export function getHardwareConnectConfig(
    callback: (config: HardwareConnectSettingsType) => void,
    success: boolean | ((error?: any) => void) = false
): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.GET_CONFIG,
        data: {
            callback,
        },
        success,
    });
}

export function setHardwareConnectConfig(config: unknown, success: boolean | ((error?: any) => void) = false): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.SET_CONFIG,
        data: {
            config,
        },
        success,
    });
}

export function hardwareConnectBroadcast(
    event: string,
    data: unknown,
    success: boolean | ((error?: any) => void) = false
): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.BROADCAST,
        data: {
            type: event,
            data,
        },
        success,
    });
}

export function exportDBF(
    file: string,
    fields: any,
    values: any,
    success: boolean | ((error?: any) => void) = false
): void {
    sendEvent({
        type: HARDWARE_CONNECT_EVENTS.DBF,
        data: {
            fileName: file,
            fields,
            values,
        },
        success,
    });
}
